import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil, Observable, finalize } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import {
  Transaction,
  TransactionFilter,
  TransactionStatusEnum,
  TransactionTypeEnum,
} from '@olv/ui-shared/models/merchant-transaction.model';
import { MerchantService } from '@olv/ui-shared/services/merchant.service';
import {
  TransactionHistoryComponent,
  TransactionHistoryConfig,
  TransactionHistoryService,
} from '@olv/ui-shared/ui-shared/transaction-history/transaction-history.component';
import * as mockDataJson from './merchant-transaction-history.mock.json';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';

interface FilterOptionsData {
  filters: {
    actionRequired: string[];
    type: string[];
    account: string[];
  };
  transactions: any[];
}

const filterOptionsData = mockDataJson as FilterOptionsData;

// Create a service adapter to bridge MerchantService to TransactionHistoryService
class MerchantTransactionServiceAdapter implements TransactionHistoryService<Transaction> {
  constructor(private merchantService: MerchantService) {}

  getTransactions(
    merchantId: string,
    filter: TransactionFilter,
  ): Observable<{
    items: Transaction[];
    totalCount: number;
  }> {
    // Convert TransactionFilter to TransactionFilter
    const merchantFilter: TransactionFilter & any = {
      skipCount: filter.skipCount,
      maxResultCount: filter.maxResultCount,
      searchTerm: filter.filter,
      type: filter.transactionType,
      actionRequired: filter.actionRequired,
      account: filter.account,
      sortColumn: filter.sorting,
    };

    return this.merchantService.getMerchantTransactions(merchantId, merchantFilter);
  }

  openSidebar(): void {
    this.merchantService.openSidebar();
  }

  closeSidebar(): void {
    this.merchantService.closeSidebar();
  }
}

@Component({
  selector: 'app-merchant-transaction-history',
  standalone: true,
  imports: [MatTooltipModule, MatIconModule, TransactionHistoryComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './merchant-transaction-history.component.html',
})
export class MerchantTransactionHistoryComponent implements OnInit, OnDestroy {
  private merchantService = inject(MerchantService);
  private route = inject(ActivatedRoute);

  merchantId = '';
  @Input() initialSearch = '';

  private readonly appCtx = inject(AppContextService);

  isMerchantApp = false;
  // Service adapter for the shared component
  merchantTransactionService: TransactionHistoryService<Transaction>;

  transactions: Transaction[] = [];

  searchText = '';

  currentSkip = 0;
  totalCount = 0;
  hasMoreTransactions = false;

  isLoading = false;
  initialLoading = true;
  error: string | null = null;

  // Configuration for the shared transaction history component
  transactionHistoryConfig: TransactionHistoryConfig = {
    title: 'Transaction History',
    showDetailsPanel: true,
    filterOptions: {
      actionRequired: filterOptionsData.filters.actionRequired,
      type: filterOptionsData.filters.type,
      account: filterOptionsData.filters.account,
    },
  };

  private destroy$ = new Subject<void>();
  selectedTransaction: Transaction | null = null;

  constructor() {
    // Create the service adapter
    this.merchantTransactionService = new MerchantTransactionServiceAdapter(this.merchantService);
  }

  ngOnInit(): void {
    const appName = this.appCtx.getAppName();
    this.isMerchantApp = appName === 'merchant';
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.merchantId = id;
      } else {
        this.error = 'Merchant ID not found in route';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTransactionSelected(transaction: Transaction): void {
    this.selectedTransaction = transaction;
  }

  loadTransactions(loadMore = false): void {
    const filter: TransactionFilter = {
      skipCount: this.currentSkip,
      maxResultCount: 20,
      filter: this.searchText?.trim() || undefined,
    };

    this.loadTransactionsWithFilter(filter, loadMore);
  }

  loadTransactionsWithFilter(filter: TransactionFilter, loadMore = false): void {
    if (!this.merchantId) {
      this.error = 'Merchant ID is required';
      this.initialLoading = false;
      return;
    }

    if (loadMore) {
      this.isLoading = true;
    } else {
      this.initialLoading = true;
      this.transactions = [];
      this.currentSkip = 0;
    }

    const filterWithSort = {
      ...filter,
    };
    const merchantIdToSend = this.isMerchantApp ? undefined : this.merchantId;
    this.merchantService
      .getMerchantTransactions(merchantIdToSend, this.isMerchantApp, filterWithSort)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.initialLoading = false;
        }),
      )
      .subscribe({
        next: (result) => {
          this.transactions = loadMore ? [...this.transactions, ...result.items] : result.items;
          this.totalCount = result.totalCount;
          this.currentSkip += result.items.length;
          this.hasMoreTransactions = this.currentSkip < this.totalCount;
          this.error = null;
        },
        error: (err) => {
          console.error('Error loading transactions:', err);
          this.error = 'Failed to load transactions. Please try again.';
        },
      });
  }

  onTransactionUpdated(transactionId: string): void {
    const updatedTransaction = this.transactions.find((t) => t.id === transactionId);

    if (updatedTransaction && this.selectedTransaction) {
      updatedTransaction.status = this.selectedTransaction.status;
    }
    this.loadTransactions();
  }
}
